import { Star, Share2, Link2, MessageCircle, Copy, Check } from "lucide-react";
import { useState } from "react";
import { getBusinessConfig } from "@/lib/site-config";
import { trackEvent } from "@/lib/tracking";

interface Props {
  /** Where this block is rendered, e.g. "footer", "reviews", "contact", "booking_success". */
  location: string;
}

/**
 * "Happy with the work? Help a local garage grow." — review + referral block.
 * Drives Google reviews and Linktree shares without gating or incentives.
 * Tracks `review_request_click`, `referral_share_click`, `linktree_click`.
 */
export function ReviewReferralBlock({ location }: Props) {
  const biz = getBusinessConfig();
  const [copied, setCopied] = useState(false);

  const shareText =
    "Woodseer Autos in Poplar helped with my car — MOTs, servicing, diagnostics and repairs. Here's their link: " +
    biz.linktreeUrl;
  const waShare = `https://wa.me/?text=${encodeURIComponent(shareText)}`;

  async function copyLinktree() {
    try {
      await navigator.clipboard.writeText(biz.linktreeUrl);
      setCopied(true);
      trackEvent("referral_share_click", { location, channel: "copy_linktree" });
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard may be unavailable */
    }
  }

  return (
    <aside
      aria-labelledby={`review-referral-${location}`}
      className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-6 md:p-8"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/15 text-primary shrink-0">
          <Star className="h-5 w-5" />
        </div>
        <div>
          <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-primary">
            Help a local garage grow
          </p>
          <h3
            id={`review-referral-${location}`}
            className="mt-1 text-lg md:text-xl font-bold text-foreground tracking-tight"
          >
            Happy with the work? A quick share goes a long way.
          </h3>
          <p className="mt-2 text-sm text-muted-foreground max-w-xl">
            Honest Google reviews and word-of-mouth from local drivers are what keep an independent
            East London garage on the map. No discounts, no pressure — just thank you.
          </p>
        </div>
      </div>

      <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <a
          href={biz.googleReviewsUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackEvent("review_request_click", { location, channel: "google" })}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 text-sm font-bold text-primary-foreground hover:bg-primary/90"
        >
          <Star className="h-4 w-4" /> Leave a Google review
        </a>
        <a
          href={waShare}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackEvent("referral_share_click", { location, channel: "whatsapp" })}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#25D366] px-4 py-3 text-sm font-bold text-white hover:bg-[#1ea952]"
        >
          <MessageCircle className="h-4 w-4" /> Share on WhatsApp
        </a>
        <a
          href={biz.linktreeUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackEvent("linktree_click", { location, channel: "linktree" })}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-card px-4 py-3 text-sm font-bold text-foreground hover:border-primary/40"
        >
          <Link2 className="h-4 w-4" /> Open Linktree
        </a>
        <button
          type="button"
          onClick={copyLinktree}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-card px-4 py-3 text-sm font-bold text-foreground hover:border-primary/40"
        >
          {copied ? <Check className="h-4 w-4 text-primary" /> : <Copy className="h-4 w-4" />}
          {copied ? "Copied!" : "Copy Linktree link"}
        </button>
      </div>

      <p className="mt-4 text-[11px] text-muted-foreground flex items-center gap-1.5">
        <Share2 className="h-3 w-3" />
        Reviews must be your honest experience — we never ask for fake or incentivised reviews.
      </p>
    </aside>
  );
}
